/**
 * FormulaClipboardBar — toolbar with Copy all / Paste actions for the
 * formula tab. Keeps copy/paste discoverable next to "Add formula group".
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, ReactNode, useMemo, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import type { FormulaConfig } from '@trafficpulse/shared';

import { Button } from '@/components/ui/layout/Button';
import { Tooltip } from '@/components/ui/Tooltip';

import { useOptionalFlowOptimizerLivePreview } from '../../context/FlowOptimizerLivePreviewContext';
import { ignorePathOptions } from '../../ignore-paths';
import { applyClipboardPayload } from '../formula-clipboard.apply';
import { useFormulaClipboard } from '../useFormulaClipboard';
import PasteFormulaMenu from './PasteFormulaMenu';

export interface FormulaClipboardBarProps {
  formula: FormulaConfig;
  onChange: (next: FormulaConfig) => void;
  /** Library set controls (Save as set… / Load set) — composed upstream. */
  libraryActions?: ReactNode;
}

const FormulaClipboardBar: FC<FormulaClipboardBarProps> = ({
  formula,
  onChange,
  libraryActions,
}) => {
  const { copyFormula } = useFormulaClipboard();
  const [copied, setCopied] = useState(false);
  // Skips travel with a copy, but only survive a paste onto a flow that has
  // those bank rows — pasting into another campaign drops them.
  const preview = useOptionalFlowOptimizerLivePreview();
  const knownBankIds = useMemo(
    () => new Set(ignorePathOptions(preview?.result?.items).map((o) => o.value)),
    [preview?.result],
  );
  const hasGroups = formula.groups.length > 0;

  const handleCopyAll = async () => {
    if (!hasGroups) return;
    await copyFormula(formula);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border/60 bg-muted/15 px-2 py-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Reuse
      </span>
      <Tooltip
        content={
          hasGroups
            ? 'Copy every group + master weights as a single JSON payload.'
            : 'Add at least one group to enable copy.'
        }
        side="bottom"
      >
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleCopyAll}
          disabled={!hasGroups}
          className="h-8 px-2.5 text-xs"
        >
          {copied ? (
            <Check className="mr-1 h-3.5 w-3.5 text-success" aria-hidden />
          ) : (
            <Copy className="mr-1 h-3.5 w-3.5" aria-hidden />
          )}
          Copy all
        </Button>
      </Tooltip>
      <PasteFormulaMenu
        hasExistingGroups={hasGroups}
        onApply={(payload) =>
          onChange(applyClipboardPayload(formula, payload, knownBankIds))
        }
      />
      {libraryActions}
      <span className="ml-auto text-[10px] text-muted-foreground">
        Paste menu shows your last 10 copies — works across campaigns.
      </span>
    </div>
  );
};

export default FormulaClipboardBar;
